<template>
  <div>
    <!-- Hidden off-screen container for image capture -->
    <div
      ref="captureArea"
      style="position: fixed; left: -9999px; top: 0; z-index: -1"
    >
      <div :style="containerStyle">
        <!-- Header -->
        <div :style="headerStyle">
          <div>
            <div :style="companyNameStyle">
              <span style="color: #d32f2f">JMB</span>
              <span style="color: #1976d2"> COMPUTERS</span>
            </div>
            <div :style="taglineStyle">
              JMB COMPUTER AND LAPTOP REPAIR SERVICES
            </div>
          </div>
          <div style="text-align: right">
            <div :style="docTitleStyle">QUOTATION SUMMARY</div>
            <div :style="modeBadgeStyle">
              {{
                form.quotation_mode === "government" ? "Government" : "Walk-in"
              }}
            </div>
          </div>
        </div>

        <!-- Customer Info -->
        <div :style="sectionTitleStyle">Customer Information</div>
        <table :style="infoTableStyle">
          <tr>
            <td :style="infoLabelStyle">Customer</td>
            <td :style="infoValueStyle">{{ form.name || "-" }}</td>
            <td :style="infoLabelStyle">Date</td>
            <td :style="infoValueStyle">{{ formattedDate }}</td>
          </tr>
          <tr>
            <td :style="infoLabelStyle">Company</td>
            <td :style="infoValueStyle">{{ form.company_name || "-" }}</td>
            <td :style="infoLabelStyle">Mode</td>
            <td :style="infoValueStyle">
              {{
                form.quotation_mode === "government"
                  ? "Government Procurement"
                  : "Walk-in Customer"
              }}
            </td>
          </tr>
          <tr v-if="form.notes">
            <td :style="infoLabelStyle">Purpose</td>
            <td :style="infoValueStyle" colspan="3">{{ form.notes }}</td>
          </tr>
        </table>

        <!-- Items Table -->
        <div :style="sectionTitleStyle">Quotation Items</div>
        <table :style="itemsTableStyle">
          <thead>
            <tr>
              <th :style="thStyle" style="width: 30px; text-align: center">
                #
              </th>
              <th :style="thStyle">Item Name</th>
              <th :style="thStyle" style="width: 40px; text-align: center">
                Qty
              </th>
              <th :style="thStyle" style="width: 45px; text-align: center">
                Unit
              </th>
              <th :style="thStyle" style="width: 100px; text-align: right">
                Unit Price
              </th>
              <th :style="thStyle" style="width: 100px; text-align: right">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-if="
                groupedItems.standalone.length === 0 &&
                groupedItems.bundles.length === 0
              "
            >
              <tr>
                <td
                  :style="tdStyle"
                  colspan="6"
                  style="text-align: center; color: #999; padding: 16px"
                >
                  No items in this quotation
                </td>
              </tr>
            </template>
            <template v-else>
              <!-- Standalone items -->
              <tr
                v-for="(item, idx) in groupedItems.standalone"
                :key="'s-' + idx"
              >
                <td :style="tdStyleCenter">{{ getRowNum(idx) }}</td>
                <td :style="tdStyle">{{ item.item_name || "-" }}</td>
                <td :style="tdStyleCenter">{{ item.quantity || 0 }}</td>
                <td :style="tdStyleCenter">{{ item.unit || "pc" }}</td>
                <td :style="tdStyleRight">
                  {{ formatNumber(item.final_quote_price_per_unit) }}
                </td>
                <td :style="tdStyleRight">
                  {{
                    formatNumber(
                      item.final_quote_price_total || item.total_amount
                    )
                  }}
                </td>
              </tr>
              <!-- Bundles -->
              <template v-for="bundle in groupedItems.bundles">
                <!-- Bundle header -->
                <tr :key="'bh-' + bundle.id">
                  <td colspan="6" :style="bundleHeaderStyle">
                    &#9654; {{ bundle.name }}
                    <span :style="bundleTypeStyle">{{ bundle.type }}</span>
                    <span style="float: right"
                      >Subtotal: {{ formatCurrency(bundle.subtotal) }}</span
                    >
                  </td>
                </tr>
                <!-- Bundle items -->
                <tr
                  v-for="(item, bIdx) in bundle.items"
                  :key="'bi-' + bundle.id + '-' + bIdx"
                >
                  <td
                    :style="{
                      ...tdStyleCenter,
                      borderLeft: '3px solid #00897b',
                    }"
                  >
                    {{
                      getRowNum(
                        groupedItems.standalone.length +
                          getBundleItemGlobalIdx(bundle, bIdx)
                      )
                    }}
                  </td>
                  <td :style="tdStyle">{{ item.item_name || "-" }}</td>
                  <td :style="tdStyleCenter">{{ item.quantity || 0 }}</td>
                  <td :style="tdStyleCenter">{{ item.unit || "pc" }}</td>
                  <td :style="tdStyleRight">
                    {{
                      hideBundleItemPrices
                        ? "-"
                        : formatNumber(item.final_quote_price_per_unit)
                    }}
                  </td>
                  <td :style="tdStyleRight">
                    {{
                      hideBundleItemPrices
                        ? "-"
                        : formatNumber(
                            item.final_quote_price_total || item.total_amount
                          )
                    }}
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>

        <!-- Financial Summary -->
        <div :style="sectionTitleStyle">Financial Summary</div>
        <table :style="finTableStyle">
          <tr v-if="form.quotation_mode === 'government' && form.abc">
            <td :style="finLabelBoldStyle">
              ABC (Approved Budget for Contract)
            </td>
            <td :style="finValueBlueStyle">
              {{ formatCurrency(form.abc) }}
            </td>
          </tr>
          <tr>
            <td :style="finLabelStyle">Total Quote Price</td>
            <td :style="finValuePrimaryStyle">
              {{ formatCurrency(calculations.total_quote_price) }}
            </td>
          </tr>
          <tr>
            <td :style="finLabelStyle">Capital Expenditure</td>
            <td :style="finValueStyle">
              {{ formatCurrency(calculations.total_capital_expenditure) }}
            </td>
          </tr>
          <tr v-if="calculations.sop_amount > 0">
            <td :style="finLabelStyle">SOP Amount</td>
            <td :style="finValueStyle">
              {{ formatCurrency(calculations.sop_amount) }}
            </td>
          </tr>
          <tr v-if="calculations.discount_amount > 0">
            <td :style="finLabelStyle">
              Discount{{
                calculations.discount_type === "percentage"
                  ? " (" + (calculations.discount_value || 0) + "%)"
                  : ""
              }}
            </td>
            <td :style="finValueStyle">
              - {{ formatCurrency(calculations.discount_amount) }}
            </td>
          </tr>
          <tr v-if="calculations.discount_amount > 0">
            <td :style="finLabelBoldStyle">Total After Discount (Customer Pays)</td>
            <td :style="finValueGrandTotalStyle">
              {{ formatCurrency(calculations.customer_total_after_discount) }}
            </td>
          </tr>

          <!-- Government mode -->
          <template v-if="form.quotation_mode === 'government'">
            <tr>
              <td :style="finLabelStyle">BIR 2307 Withholding (6%)</td>
              <td :style="finValueStyle">
                {{ formatCurrency(calculations.bir_2307_amount) }}
              </td>
            </tr>
            <tr>
              <td :style="finLabelStyle">Cheque Amount</td>
              <td :style="finValueBlueStyle">
                {{ formatCurrency(calculations.cheque_amount) }}
              </td>
            </tr>
            <tr>
              <td :style="finLabelStyle">VAT Payable</td>
              <td :style="finValueStyle">
                {{ formatCurrency(calculations.total_vat_payable) }}
              </td>
            </tr>
            <tr>
              <td :style="finLabelStyle">
                Income Tax ({{ calculations.income_tax_percentage || 0 }}%)
              </td>
              <td :style="finValueStyle">
                {{ formatCurrency(calculations.income_tax_amount) }}
              </td>
            </tr>
          </template>

          <!-- Walk-in VAT purchase -->
          <template
            v-if="
              form.quotation_mode === 'walk_in' && calculations.is_vat_purchase
            "
          >
            <tr>
              <td :style="finLabelStyle">VAT (12%)</td>
              <td :style="finValueStyle">
                {{ formatCurrency(calculations.vat_amount_walk_in) }}
              </td>
            </tr>
            <tr>
              <td :style="finLabelStyle">
                Income Tax ({{ calculations.income_tax_percentage || 0 }}%)
              </td>
              <td :style="finValueStyle">
                {{ formatCurrency(calculations.income_tax_amount) }}
              </td>
            </tr>
            <tr>
              <td :style="finLabelBoldStyle">Grand Total (Customer Pays)</td>
              <td :style="finValueGrandTotalStyle">
                {{ formatCurrency(grandTotalCustomerPays) }}
              </td>
            </tr>
          </template>

          <!-- Custom Deductions: Auto-Deducted -->
          <template v-if="autoDeductDeductions.length > 0">
            <tr>
              <td :style="finDividerStyle" colspan="2">
                Auto-Deducted from Payment
              </td>
            </tr>
            <tr v-for="ded in autoDeductDeductions" :key="ded.id">
              <td :style="finLabelStyle">
                {{ ded.name }}
                {{ ded.type === "percentage" ? "(" + ded.value + "%)" : "" }}
              </td>
              <td :style="finValueStyle">
                {{ formatCurrency(ded.computed_amount) }}
              </td>
            </tr>
          </template>

          <!-- Custom Deductions: Capital Expenditure -->
          <template v-if="capexDeductions.length > 0">
            <tr>
              <td :style="finDividerStyle" colspan="2">
                Capital Expenditure (Upfront)
              </td>
            </tr>
            <tr v-for="ded in capexDeductions" :key="ded.id">
              <td :style="finLabelStyle">
                {{ ded.name }}
                {{ ded.type === "percentage" ? "(" + ded.value + "%)" : "" }}
              </td>
              <td :style="finValueStyle">
                {{ formatCurrency(ded.computed_amount) }}
              </td>
            </tr>
          </template>

          <!-- Net Profit -->
          <tr>
            <td :style="finLabelBoldStyle">Net Profit</td>
            <td
              :style="
                calculations.net_profit >= 0
                  ? finValueGreenStyle
                  : finValueRedStyle
              "
            >
              {{ formatCurrency(calculations.net_profit) }}
            </td>
          </tr>
          <tr>
            <td :style="finLabelStyle">Net Profit Margin</td>
            <td
              :style="
                calculations.net_profit_margin >= 0
                  ? finValueGreenStyle
                  : finValueRedStyle
              "
            >
              {{ formatPercent(calculations.net_profit_margin) }}
            </td>
          </tr>
          <tr>
            <td :style="finLabelStyle">Markup</td>
            <td
              :style="
                calculations.markup >= 0 ? finValueGreenStyle : finValueRedStyle
              "
            >
              {{ formatPercent(calculations.markup) }}
            </td>
          </tr>
        </table>

        <!-- Remarks -->
        <div v-if="form.remarks" :style="remarksStyle">
          <strong>Remarks:</strong> {{ form.remarks }}
        </div>

        <!-- Footer -->
        <div :style="footerStyle">
          For internal approval only &mdash; Generated {{ generatedDate }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toBlob } from "html-to-image";

export default {
  props: {
    form: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    calculations: {
      type: Object,
      required: true,
    },
    customDeductions: {
      type: Array,
      default: () => [],
    },
    hideBundleItemPrices: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      capturing: false,
    };
  },
  computed: {
    formattedDate() {
      if (!this.form.quotation_date) return "-";
      const d = new Date(this.form.quotation_date + "T00:00:00");
      return d.toLocaleDateString("en-PH", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    generatedDate() {
      return new Date().toLocaleDateString("en-PH", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    grandTotalCustomerPays() {
      const price = parseFloat(this.calculations.total_quote_price) || 0;
      const vat = parseFloat(this.calculations.vat_amount_walk_in) || 0;
      return price + vat;
    },
    autoDeductDeductions() {
      if (!this.customDeductions) return [];
      return this.customDeductions.filter(
        (d) => d.deduction_mode !== "capital_expenditure"
      );
    },
    capexDeductions() {
      if (!this.customDeductions) return [];
      return this.customDeductions.filter(
        (d) => d.deduction_mode === "capital_expenditure"
      );
    },
    groupedItems() {
      const items = this.items;
      if (!items || items.length === 0) return { standalone: [], bundles: [] };

      const standalone = [];
      const bundleMap = {};

      items.forEach((item) => {
        if (item.bundle_id) {
          if (!bundleMap[item.bundle_id]) {
            bundleMap[item.bundle_id] = {
              id: item.bundle_id,
              name: item.bundle_name || "Bundle",
              type: item.bundle_type || "Custom",
              items: [],
              subtotal: 0,
            };
          }
          bundleMap[item.bundle_id].items.push(item);
          bundleMap[item.bundle_id].subtotal +=
            parseFloat(item.final_quote_price_total) ||
            parseFloat(item.total_amount) ||
            0;
        } else {
          standalone.push(item);
        }
      });

      Object.values(bundleMap).forEach((bundle) => {
        bundle.items.sort(
          (a, b) => (a.bundle_sort_order || 0) - (b.bundle_sort_order || 0)
        );
      });

      return { standalone, bundles: Object.values(bundleMap) };
    },

    // ---- Inline styles (for html-to-image compatibility) ----
    containerStyle() {
      return {
        width: "700px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#2c3e50",
        fontSize: "12px",
        lineHeight: "1.4",
        background: "#ffffff",
        padding: "24px",
      };
    },
    headerStyle() {
      return {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        paddingBottom: "10px",
        borderBottom: "3px double #1976d2",
        marginBottom: "16px",
      };
    },
    companyNameStyle() {
      return {
        fontSize: "20px",
        fontWeight: "bold",
        letterSpacing: "1.5px",
      };
    },
    taglineStyle() {
      return {
        fontSize: "9px",
        color: "#555",
        fontWeight: "600",
      };
    },
    docTitleStyle() {
      return {
        fontSize: "16px",
        fontWeight: "700",
        color: "#1976d2",
        letterSpacing: "1px",
        marginBottom: "4px",
      };
    },
    modeBadgeStyle() {
      const isGov = this.form.quotation_mode === "government";
      return {
        display: "inline-block",
        padding: "2px 12px",
        borderRadius: "3px",
        color: "white",
        fontSize: "10px",
        fontWeight: "600",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        background: isGov ? "#1976d2" : "#4caf50",
      };
    },
    sectionTitleStyle() {
      return {
        fontSize: "11px",
        fontWeight: "700",
        color: "#1976d2",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        paddingBottom: "4px",
        borderBottom: "2px solid #e3f2fd",
        marginBottom: "8px",
        marginTop: "16px",
      };
    },
    infoTableStyle() {
      return {
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "4px",
      };
    },
    infoLabelStyle() {
      return {
        fontSize: "9px",
        fontWeight: "700",
        color: "#888",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        padding: "4px 8px",
        width: "80px",
        verticalAlign: "top",
      };
    },
    infoValueStyle() {
      return {
        fontSize: "12px",
        color: "#2c3e50",
        fontWeight: "500",
        padding: "4px 8px",
      };
    },
    itemsTableStyle() {
      return {
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "4px",
      };
    },
    thStyle() {
      return {
        background: "linear-gradient(to bottom, #1976d2, #1565c0)",
        color: "white",
        padding: "6px 8px",
        fontWeight: "600",
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        textAlign: "left",
      };
    },
    tdStyle() {
      return {
        border: "1px solid #e0e0e0",
        padding: "5px 8px",
        fontSize: "11px",
      };
    },
    tdStyleCenter() {
      return { ...this.tdStyle, textAlign: "center" };
    },
    tdStyleRight() {
      return {
        ...this.tdStyle,
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
      };
    },
    bundleHeaderStyle() {
      return {
        background: "linear-gradient(135deg, #00897b, #00695c)",
        color: "white",
        padding: "6px 10px",
        fontWeight: "600",
        fontSize: "10px",
      };
    },
    bundleTypeStyle() {
      return {
        background: "rgba(255,255,255,0.2)",
        padding: "1px 8px",
        borderRadius: "3px",
        fontSize: "9px",
        marginLeft: "8px",
      };
    },
    finTableStyle() {
      return {
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "4px",
      };
    },
    finLabelStyle() {
      return {
        padding: "5px 8px",
        fontSize: "11px",
        color: "#3c4043",
        borderBottom: "1px solid #f0f0f0",
      };
    },
    finLabelBoldStyle() {
      return {
        ...this.finLabelStyle,
        fontWeight: "700",
        fontSize: "12px",
        borderTop: "2px solid #333",
        borderBottom: "none",
        paddingTop: "8px",
      };
    },
    finDividerStyle() {
      return {
        padding: "6px 8px 2px",
        fontSize: "9px",
        fontWeight: "600",
        color: "#888",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        borderBottom: "1px dashed #e0e0e0",
      };
    },
    finValueStyle() {
      return {
        padding: "5px 8px",
        fontSize: "11px",
        color: "#202124",
        fontWeight: "500",
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
        borderBottom: "1px solid #f0f0f0",
        width: "160px",
      };
    },
    finValuePrimaryStyle() {
      return {
        ...this.finValueStyle,
        color: "#1976d2",
        fontWeight: "700",
        fontSize: "13px",
      };
    },
    finValueBlueStyle() {
      return { ...this.finValueStyle, color: "#1976d2", fontWeight: "600" };
    },
    finValueGrandTotalStyle() {
      return {
        ...this.finValueStyle,
        color: "#1976d2",
        fontWeight: "700",
        fontSize: "13px",
        borderTop: "2px solid #333",
        borderBottom: "none",
        paddingTop: "8px",
      };
    },
    finValueGreenStyle() {
      return {
        ...this.finValueStyle,
        color: "#388e3c",
        fontWeight: "700",
        fontSize: "13px",
        borderTop: "2px solid #333",
        borderBottom: "none",
        paddingTop: "8px",
      };
    },
    finValueRedStyle() {
      return {
        ...this.finValueStyle,
        color: "#d32f2f",
        fontWeight: "700",
        fontSize: "13px",
        borderTop: "2px solid #333",
        borderBottom: "none",
        paddingTop: "8px",
      };
    },
    remarksStyle() {
      return {
        marginTop: "12px",
        padding: "8px 12px",
        background: "#fffde7",
        border: "1px solid #fff9c4",
        borderLeft: "3px solid #ffa726",
        fontSize: "10px",
        color: "#333",
        lineHeight: "1.4",
      };
    },
    footerStyle() {
      return {
        marginTop: "16px",
        paddingTop: "8px",
        borderTop: "1px solid #ddd",
        textAlign: "center",
        color: "#aaa",
        fontSize: "9px",
        fontStyle: "italic",
      };
    },
  },
  methods: {
    getRowNum(idx) {
      return idx + 1;
    },
    getBundleItemGlobalIdx(bundle, bIdx) {
      // Calculate the global index offset for bundle items
      const bundles = this.groupedItems.bundles;
      let offset = 0;
      for (let i = 0; i < bundles.length; i++) {
        if (bundles[i].id === bundle.id) {
          return offset + bIdx;
        }
        offset += bundles[i].items.length;
      }
      return offset + bIdx;
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "P " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    formatNumber(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatPercent(val) {
      const num = parseFloat(val) || 0;
      return num.toFixed(2) + "%";
    },
    async copyAsImage() {
      if (this.capturing) return;
      this.capturing = true;

      try {
        const node = this.$refs.captureArea.firstElementChild;

        const blob = await toBlob(node, {
          backgroundColor: "#ffffff",
          pixelRatio: 2,
          style: {
            // Ensure the off-screen element is rendered properly
            transform: "none",
          },
        });

        if (!blob) {
          throw new Error("Failed to generate image");
        }

        // Try Electron's native clipboard first (works reliably in Electron)
        let copied = false;
        try {
          // eslint-disable-next-line no-undef
          const { clipboard, nativeImage } = window.require("electron");
          const arrayBuffer = await blob.arrayBuffer();
          const buffer = Buffer.from(arrayBuffer);
          const image = nativeImage.createFromBuffer(buffer);
          clipboard.writeImage(image);
          copied = true;
          this.$emit(
            "success",
            "Quotation summary copied to clipboard as image!"
          );
        } catch (electronErr) {
          // Not in Electron or nodeIntegration disabled, try browser API
        }

        if (!copied) {
          // Try browser Clipboard API
          if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
            try {
              await navigator.clipboard.write([
                new ClipboardItem({ "image/png": blob }), // eslint-disable-line no-undef
              ]);
              copied = true;
              this.$emit(
                "success",
                "Quotation summary copied to clipboard as image!"
              );
            } catch (clipErr) {
              // Clipboard API also failed, will fall back to download
            }
          }
        }

        if (!copied) {
          // Fallback: download the image
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "quotation-summary.png";
          link.click();
          URL.revokeObjectURL(url);
          this.$emit(
            "success",
            "Clipboard not supported. Image downloaded instead."
          );
        }
      } catch (err) {
        console.error("Image export error:", err);
        this.$emit(
          "error",
          "Failed to copy image: " + (err.message || "Unknown error")
        );
      } finally {
        this.capturing = false;
      }
    },
  },
};
</script>
